<template>
    <div class="payment-shell">
        <!-- Page autonome (hors tableau de bord) : cet en-tête tient lieu de repère et de sortie. -->
        <header class="payment-topbar">
            <router-link to="/analyse-patrimoniale/dashboards" class="payment-brand" aria-label="patrimonea by Gwem">
                <img src="@/assets/logo.png" alt="patrimonea by Gwem" class="payment-brand__logo" width="160" height="72" />
            </router-link>
        </header>

        <div class="payment-page">
            <div class="payment-header">
                <h1 class="payment-title">Activez votre accompagnement</h1>
                <p class="page-description">
                    Choisissez votre offre. Votre diagnostic est
                    lancé dès la confirmation du paiement.
                </p>
            </div>

            <div class="stepper" role="list" aria-label="Étapes du paiement">
                <template v-for="(label, index) in stepLabels" :key="label">
                    <div class="stepper__line" v-if="index > 0" aria-hidden="true" />
                    <div
                        role="listitem"
                        :class="['step-chip', { active: step === index + 1, done: step > index + 1 }]"
                    >
                        <span class="step-chip__num">{{ index + 1 }}</span>
                        <span class="step-chip__label">{{ label }}</span>
                    </div>
                </template>
            </div>

            <p v-if="quoteError" class="payment-error" role="alert">{{ quoteError }}</p>

            <!-- ÉTAPE 1 — Offre -->
            <!-- ÉTAPE 1 — Offre (comme PaymentPage) -->
      <section v-if="step === 1" class="card">
        <h2 class="section-title">Quelle formule vous correspond ?</h2>

        <div class="offer-grid">
          <button v-for="offerItem in offers" :key="offerItem.id" type="button"
            :class="['offer-card', { active: selectedOffer === offerItem.id }]"
            :aria-pressed="selectedOffer === offerItem.id" @click="selectedOffer = offerItem.id">
            <span v-if="offerItem.highlight" class="offer-card__badge">Le plus choisi</span>
            <span class="offer-card__audience">{{ offerItem.audience }}</span>
            <span class="offer-card__name">{{ offerItem.label }}</span>
            <span class="offer-card__price-muted">
                {{ formatAmount(offerItem.upfront) }} en une fois +
            </span>
            <span class="offer-card__price">
              <strong>{{ formatAmount(offerItem.monthly) }}</strong> FCFA {{ pricePeriodLabel(offerItem.id) }}
            </span>
            <span class="offer-card__pitch">{{ offerItem.pitch }}</span>
            <span
              class="offer-card__toggle"
              role="button"
              tabindex="0"
              @click.stop="toggleOfferFeatures(offerItem.id)"
              @keydown.enter.space.prevent="toggleOfferFeatures(offerItem.id)"
            >
              {{ expandedOffers.has(offerItem.id) ? 'Voir moins' : 'Voir plus' }}
            </span>
            <ul v-if="expandedOffers.has(offerItem.id)" class="offer-card__list">
              <li v-for="feature in offerItem.features" :key="feature">{{ feature }}</li>
            </ul>
          </button>
        </div>

        <div class="nav-actions">
          <button type="button" class="btn-secondary" @click="onBackFromOffers">Retour</button>
          <button type="button" class="btn-next" @click="confirmOffer">Continuer</button>
        </div>
      </section>

      <!-- ÉTAPE 2 — Modalité (sauf offre Accès : paiement unique) -->
      <section v-if="step === planStep" class="card">
        <h2 class="section-title">Comment souhaitez-vous régler ?</h2>
        <p class="muted">
          Offre <strong>{{ offerLabel }}</strong> 
          <!-- {{ formatAmount(totalNetDisplayAmount) }} FCFA sur douze mois, quelle que soit la formule. -->
        </p>
        <div class="plan-options">
          <button v-for="plan in planOptions" :key="plan.id" type="button"
            :class="['plan-btn', { active: selectedPlan === plan.id }]" :aria-pressed="selectedPlan === plan.id"
            @click="selectedPlan = plan.id">
            <span class="plan-title">{{ plan.label }}</span>
            <span class="plan-subtitle">{{ plan.subtitle }}</span>
            <span class="plan-price">{{ plan.price }}</span>
          </button>
        </div>
        <div class="nav-actions">
          <button type="button" class="btn-secondary" @click="goToStep(1)">Retour</button>
          <button type="button" class="btn-next" @click="confirmPlan">Continuer</button>
        </div>
      </section>

            <!-- ÉTAPE 3 — Coordonnées -->
            <section v-if="step === 3" class="card">
                <h2 class="section-title">Vérifiez vos informations</h2>
                <div class="form-group">
                    <label for="last-name">Nom</label>
                    <input type="text" id="last-name" name="last-name" v-model="userLastName" required autocomplete="family-name">
                </div>
                <div class="form-group">
                    <label for="first-name">Prénom(s)</label>
                    <input type="text" id="first-name" name="first-name" v-model="userFirstName" required autocomplete="given-name">
                </div>
                <div class="form-group">
                    <label for="email">Email</label>
                    <input type="email" id="email" name="email" v-model="userEmail" required>
                </div>
                <div class="form-group">
                    <label for="phone">Téléphone</label>
                    <input type="text" id="phone" name="phone" v-model="userPhone" required>
                </div>

                <div class="nav-actions">
                    <button class="btn-secondary" @click="goToStep(2)">Retour</button>
                    <button class="btn-next" @click="goToStep(4)">Continuer</button>
                </div>
            </section>

            <!-- ÉTAPE 4 — Moyen de paiement -->
            <section v-if="step === 4" class="card">
                <h2 class="section-title">Montant et moyen de paiement</h2>

                <div v-if="mailSent" class="rib-success">
                    <i class="fas fa-check-circle"></i>
                    <div>
                        <p>
                            Un lien de paiement a été envoyé à <strong>{{ userEmail }}</strong>.
                            Vérifiez votre boîte de réception pour finaliser le règlement.
                        </p>
                        <!-- <router-link to="/analyse-patrimoniale/dashboards" class="rib-success__link">
                            Retour au tableau de bord
                        </router-link> -->
                    </div>
                </div>

                <template v-else>
                <div class="summary-box">
                    <p><strong>Offre :</strong> {{ offerLabel }}</p>
                    <p><strong>Formule :</strong> {{ selectedPlanLabel }}</p>
                    <!-- <p><strong>Total sur douze mois :</strong> {{ formatAmount(totalDisplayAmount) }} FCFA</p> -->
                    <p><strong>À régler maintenant :</strong> {{ formatAmount(displayAmount) }} FCFA</p>
                    <p v-if="selectedPlan !== 'total'" class="muted">
                        Puis {{ instalmentSchedule }}.
                    </p>
                </div>

                <div class="payment-options">
                    <h3 class="payment-options__title">Méthodes de paiement</h3>
                    <div class="payment-methods">
                        <button
                            v-for="method in visiblePaymentMethods"
                            :key="method.id"
                            type="button"
                            :class="['method-btn', { active: selectedMethod === method.id, 'method-btn--text': !method.img }]"
                            :aria-pressed="selectedMethod === method.id"
                            @click="selectMethod(method.id)"
                        >
                            <img v-if="method.img" :src="method.img" :alt="method.label" class="method-icon" />
                            <i v-else class="fas fa-university method-icon-fa" aria-hidden="true"></i>
                            {{ method.label }}
                        </button>
                    </div>
                </div>

                <!-- RIB / virement bancaire
                <div v-if="selectedMethod === 'virement'" class="rib-box">
                    <h4 class="rib-title"><i class="fas fa-university"></i> Coordonnées bancaires (RIB)</h4>
                    <p class="rib-intro muted">
                        Effectuez un virement du montant indiqué ci-dessus vers le compte suivant.
                        Indiquez impérativement le libellé pour identifier votre paiement.
                        Pour un virement depuis l’étranger, le <strong>code SWIFT</strong> (identique au <strong>BIC</strong>) est souvent demandé par la banque donneuse d’ordre.
                    </p>
                    <dl class="rib-dl">
                        <dt>Titulaire du compte</dt>
                        <dd>{{ ribInfo.titulaire }}</dd>
                        <dt>Banque</dt>
                        <dd>{{ ribInfo.banque }}</dd>
                        <dt>IBAN</dt>
                        <dd class="rib-mono">
                            <span>{{ ribInfo.iban }}</span>
                            <button type="button" class="btn-copy" @click="copyToClipboard(ribInfo.iban, 'IBAN')">Copier</button>
                        </dd>
                        <dt>Code SWIFT / BIC</dt>
                        <dd class="rib-mono rib-row--swift">
                            <template v-if="bicSwiftDisplay">
                                <span>{{ bicSwiftDisplay }}</span>
                                <button type="button" class="btn-copy" @click="copyToClipboard(bicSwiftDisplay, 'SWIFT/BIC')">Copier</button>
                            </template>
                            <span v-else class="rib-placeholder">
                                Code SWIFT / BIC non disponible pour le moment. Contactez-nous si votre banque le demande pour un virement international.
                            </span>
                        </dd>
                    </dl>
                    <button type="button" class="btn-outline-full" @click="copyFullRib">
                        <i class="fas fa-copy"></i> Copier toutes les coordonnées
                    </button>
                    <p v-if="copyFeedback" class="copy-feedback">{{ copyFeedback }}</p>
                </div> -->

                <!-- <div v-if="virementSuccess" class="rib-success">
                    <i class="fas fa-check-circle"></i>
                    <div>
                        <p>
                            Merci. Dès réception et validation du virement, votre analyse patrimoniale sera activée.
                            Vous pourrez ensuite signer le contrat de souscription.
                        </p>
                        <router-link
                            to="/analyse-patrimoniale/dashboards"
                            class="rib-success__link"
                        >
                            Retour au tableau de bord
                        </router-link>
                    </div>
                </div> -->

                <div class="payment-actions">
                    <button class="btn-secondary" @click="goToStep(3)">Retour</button>
                    <button
                        v-if="selectedMethod !== 'virement'"
                        type="button"
                        class="btn-pay"
                        :disabled="!canInitPayment"
                        @click="showContratPopup = true"
                    >
                        <i class="fas fa-lock"></i> Payer {{ formatAmount(displayAmount) }} FCFA
                    </button>
                    <button
                        v-else
                        type="button"
                        class="btn-pay btn-pay--secondary"
                        :disabled="paying"
                        @click="confirmVirement"
                    >
                        <i class="fas fa-check"></i> J’ai effectué le virement
                    </button>
                </div>
                <p v-if="paymentError" class="payment-error">{{ paymentError }}</p>
                </template>
            </section>
        </div>

        <PopContratSouscription
            :open="showContratPopup"
            :required="false"
            :busy="paying"
            @close="showContratPopup = false"
            @accepted="onContratAccepted"
        />
    </div>
</template>

<script>
import axios from 'axios';
import { createSubmitGuard, formatThrottleMessage } from '@/utils/submitGuard';
import { safeErrorMessage } from '@/utils/errorMessages';
import {
    offersForIncome,
    allowedPlansForOffer,
    getOffer,
    normalizeOffer,
} from '@/utils/paymentPricing';
import { trackBeginCheckout } from '@/utils/analytics';
import PopContratSouscription from '@/views/patrimoine/client/PopContratSouscription.vue';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { enqueuePendingSignature } from '@/utils/pendingSignatures';

const paymentSubmitGuard = createSubmitGuard(3000);

/** Codes `channel` attendus par `/api/payments/add-payer/` (alignés côté API). */
const PAYMENT_CHANNEL_BY_METHOD = {
    omci: 'OMCI',
    waveci: 'WAVECI',
    card: 'CARD',
};

const PLAN_META = {
    total: { label: 'Paiement total', subtitle: 'Règlement en une fois' },
    quarterly: { label: 'Par trimestre', subtitle: 'Versement initial puis échéances trimestrielles' },
    monthly: { label: 'Par mois', subtitle: 'Versement initial puis mensualités' },
};

export default {
    components: { PopContratSouscription },
    data() {
        return {
            step: 1,
            showContratPopup: false,
            mailSent: false,
            expandedOffers: new Set(),
            stepLabels: ['Offre', 'Règlement', 'Coordonnées', 'Paiement'],
            offers: offersForIncome(),
            offer: 'persona',
            selectedOffer: 'persona',
            offerLocked: false,
            savingOffer: false,
            selectedMethod: 'omci',
            selectedPlan: 'total',
            paymentMethods: [
                { id: 'omci', label: 'Orange Money', img: require('../../images/Orange.png') },
                { id: 'waveci', label: 'Wave', img: require('../../images/Wave.png') },
                { id: 'card', label: 'Visa / Mastercard', img: require('../../images/Visa.png') },
                // { id: 'virement', label: 'Virement bancaire (RIB)', img: null },
            ],
            /** À personnaliser avec le RIB réel de l’entreprise (Patrimonea / Gwem). */
            ribInfo: {
                titulaire: 'SAS patrimonea by Gwem',
                banque: "BGFIBank CÔTE D'IVOIRE",
                iban: 'CI93 CI192 01008 002010522201 77',
                bic: 'BGFICIAB',
                swift: 'BGFICIAB',
            },
            copyFeedback: '',
            virementSuccess: false,
            // Données de l'utilisateur à pré-remplir
            userFirstName: '',
            userLastName: '',
            userEmail: '',
            userPhone: '',
            // Renvoyés par /api/payments/quote/ : c'est le serveur qui fait foi sur les montants.
            totalAmount: 0,
            serverUpfront: 0,
            serverInstalments: null,
            serverPlans: null,
            paying: false,
            paymentError: '',
            quoteLoaded: false,
            quoteError: '',
        };
    },
    computed: {
        currentOffer() {
            return getOffer(this.offer);
        },
        offerLabel() {
            return this.currentOffer.label;
        },
        /** Le serveur fait foi sur les montants ; le catalogue sert de repli hors ligne. */
        totalDisplayAmount() {
            return Number(this.totalAmount || 0) || this.currentOffer.total;
        },
        totalNetDisplayAmount() {
            return this.currentOffer?.totalnet || 0;
        },
        upfrontDisplayAmount() {
            return Number(this.serverUpfront || 0) || this.currentOffer.upfront;
        },
        currentInstalments() {
            return this.serverInstalments || this.currentOffer.instalments || {};
        },
        planOptions() {
            const plans = this.serverPlans || allowedPlansForOffer(this.offer);
            return plans.map((id) => ({
                id,
                label: PLAN_META[id].label,
                subtitle: PLAN_META[id].subtitle,
                price: this.planPriceLabel(id),
            }));
        },
        selectedPlanLabel() {
            const plan = this.planOptions.find((p) => p.id === this.selectedPlan);
            return plan ? plan.label : PLAN_META.total.label;
        },
        instalmentSchedule() {
            return this.scheduleFor(this.selectedPlan);
        },
        displayAmount() {
            if (!this.quoteLoaded) return 0;
            return this.selectedPlan === 'total' ? this.totalDisplayAmount : this.upfrontDisplayAmount;
        },
        canInitPayment() {
            return this.quoteLoaded && this.displayAmount > 0 && !this.paying;
        },
        /** Offres facturables ici (persona/duo/pro) : jamais "access", donc pas
         * de saut d'étape — la modalité de règlement est toujours l'étape 2. */
        planStep() {
            return 2;
        },
        /** Libellé à indiquer sur le virement (identification du client). */
        ribLibelleVirement() {
            const parts = ['PATRIMONEA', this.userEmail || '', this.offerLabel || ''].filter(Boolean);
            return parts.join(' — ');
        },
        /** SWIFT et BIC désignent le même identifiant bancaire (8 ou 11 caractères). */
        bicSwiftDisplay() {
            const raw = this.ribInfo?.bic || this.ribInfo?.swift || '';
            return String(raw).trim();
        },
        /** Venant de la validation finale de FormApat : paiement immédiatement suivi
         * de la signature du contrat, donc le virement (activation différée, validée
         * manuellement par un admin) n'a pas sa place ici. */
        isSubmitAnalysisFlow() {
            return this.$route.query.context === 'submit-analysis';
        },
        visiblePaymentMethods() {
            if (!this.isSubmitAnalysisFlow) return this.paymentMethods;
            return this.paymentMethods.filter((m) => m.id !== 'virement');
        },
    },
    async created() {
        if (!isAuthenticatedSession()) {
            this.$router.replace('/connexion');
            return;
        }
        await this.loadUserProfile();
        await this.loadPaymentQuote();
    },
    methods: {
        goToStep(step) {
            this.step = step;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        formatAmount(value) {
            return new Intl.NumberFormat('fr-FR').format(Number(value || 0));
        },
        pricePeriodLabel(offerId) {
            const id = String(offerId || '');
            if (id.startsWith('pro')) return 'en 3 fois';
            if (id === 'access') return '';
            return '/mois';
        },
        /** Nombre d'échéances déduit du solde restant : 12 mensualités, 4 ou 3 trimestres. */
        instalmentCount(planId) {
            const amount = this.currentInstalments[planId];
            if (!amount) return 0;
            return Math.round((this.totalDisplayAmount - this.upfrontDisplayAmount) / amount);
        },
        scheduleFor(planId) {
            const amount = this.currentInstalments[planId];
            if (!amount) return '';
            const count = this.instalmentCount(planId);
            const unit = planId === 'monthly' ? 'mensualités' : 'versements trimestriels';
            return `${count} ${unit} de ${this.formatAmount(amount)} FCFA`;
        },
        planPriceLabel(planId) {
            if (planId === 'total') {
                return `${this.formatAmount(this.totalDisplayAmount)} FCFA`;
            }
            return `${this.formatAmount(this.upfrontDisplayAmount)} FCFA aujourd’hui, puis ${this.scheduleFor(planId)}`;
        },
        applyQuote(data) {
            if (data?.offre) {
                this.offer = normalizeOffer(data.offre);
                this.selectedOffer = this.offer;
            }
            if (data?.amount != null) this.totalAmount = Number(data.amount) || 0;
            if (data?.upfront != null) this.serverUpfront = Number(data.upfront) || 0;
            if (data?.instalments) this.serverInstalments = data.instalments;
            if (Array.isArray(data?.allowed_plans) && data.allowed_plans.length) {
                this.serverPlans = data.allowed_plans;
            }
            if (data?.offer_locked != null) this.offerLocked = Boolean(data.offer_locked);
            this.quoteLoaded = this.totalDisplayAmount > 0;
            // Une offre sans mensualisation ne doit pas conserver un plan devenu invalide.
            if (!this.planOptions.some((plan) => plan.id === this.selectedPlan)) {
                this.selectedPlan = 'total';
            }
        },
        async loadPaymentQuote() {
            try {
                if (!isAuthenticatedSession()) return;
                const { data } = await axios.get('/api/payments/quote/', {
                    withCredentials: true,
                    timeout: 30000,
                });
                this.applyQuote(data);
                this.quoteError = '';
            } catch (e) {
                this.quoteLoaded = false;
                this.quoteError = "Impossible de charger le tarif. Réessayez ou contactez le support.";
            }
        },
        onBackFromOffers() {
            this.$router.push('/analyse-patrimoniale/dashboards');
        },
        confirmPlan() {
            if (!this.selectedPlan) {
                this.quoteError = 'Choisissez une modalité de règlement.';
                return;
            }
            this.quoteError = '';
            this.goToStep(3);
        },
        toggleOfferFeatures(offerId) {
            if (this.expandedOffers.has(offerId)) {
                this.expandedOffers.delete(offerId);
            } else {
                this.expandedOffers.add(offerId);
            }
        },
        /**
         * L'offre est enregistrée côté serveur avant de continuer : c'est elle qui
         * détermine le montant facturé, le client ne peut pas l'imposer plus tard.
         */
        async confirmOffer() {
            if (this.offerLocked && this.selectedOffer === this.offer) {
                this.goToStep(2);
                return;
            }
            if (this.selectedOffer === this.offer && this.quoteLoaded) {
                this.goToStep(2);
                return;
            }
            this.savingOffer = true;
            this.quoteError = '';
            try {
                const { data } = await axios.post(
                    '/api/payments/select-offer/',
                    { offre: this.selectedOffer },
                    { withCredentials: true, timeout: 30000 }
                );
                this.applyQuote(data);
                this.goToStep(2);
            } catch (error) {
                this.quoteError = safeErrorMessage(error, "Impossible d'enregistrer votre offre. Réessayez dans un instant.");
                if (error.response?.data?.code === 'offer_locked') {
                    this.offerLocked = true;
                    this.selectedOffer = this.offer;
                }
            } finally {
                this.savingOffer = false;
            }
        },
        async loadUserProfile() {
            try {
                if (!isAuthenticatedSession()) {
                    this.$router.replace('/connexion');
                    return;
                }

                const response = await axios.get('/api/profile/', { withCredentials: true });
                const data = response.data;
                this.userFirstName = data.first_name;
                this.userLastName = data.last_name;
                this.userEmail = data.email;
                this.userPhone = data.tel;
            } catch (error) {
                console.error('Erreur lors du chargement du profil utilisateur:', error);
            }
        },
        selectMethod(method) {
            this.selectedMethod = method;
            this.virementSuccess = false;
            this.copyFeedback = '';
        },
        async copyToClipboard(text, label) {
            if (!text) return;
            try {
                await navigator.clipboard.writeText(String(text));
                this.copyFeedback = `${label} copié dans le presse-papiers.`;
            } catch (_) {
                this.copyFeedback = 'Copie impossible. Sélectionnez le texte manuellement.';
            }
            clearTimeout(this._copyFbTimer);
            this._copyFbTimer = setTimeout(() => { this.copyFeedback = ''; }, 3500);
        },
        copyFullRib() {
            const lines = [
                `Titulaire: ${this.ribInfo.titulaire}`,
                `Banque: ${this.ribInfo.banque}`,
                `IBAN: ${this.ribInfo.iban}`,
                this.bicSwiftDisplay ? `SWIFT/BIC: ${this.bicSwiftDisplay}` : '',
                `Montant: ${this.formatAmount(this.displayAmount)} FCFA`,
                `Libellé: ${this.ribLibelleVirement}`,
            ].filter(Boolean);
            this.copyToClipboard(lines.join('\n'), 'Coordonnées');
        },
        confirmVirement() {
            this.processVirementConfirmation();
        },
        async processVirementConfirmation() {
            this.paymentError = '';
            if (!this.quoteLoaded) {
                this.paymentError = this.quoteError || 'Tarif non chargé.';
                return;
            }
            if (!isAuthenticatedSession()) {
                this.paymentError = 'Session expirée. Veuillez vous reconnecter.';
                this.$router.push('/connexion');
                return;
            }
            const guarded = await paymentSubmitGuard.run(async () => {
                this.paying = true;
                try {
                    await axios.post(
                        '/api/payments/confirm-virement/',
                        { payment_plan: this.selectedPlan },
                        { withCredentials: true, timeout: 30000 }
                    );
                    this.virementSuccess = true;
                    this.$nextTick(() => {
                        const el = this.$el.querySelector('.rib-success');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    });
                } catch (error) {
                    this.paymentError =
                        formatThrottleMessage(error) ||
                        safeErrorMessage(error, 'Impossible d’enregistrer votre déclaration de virement. Réessayez ou contactez le support.');
                } finally {
                    this.paying = false;
                }
            });
            if (!guarded.ok) {
                this.paymentError = 'Veuillez patienter avant une nouvelle tentative.';
            }
        },
        /** Signature du contrat de souscription, requise avant tout paiement
         * carte/Orange Money/Wave — même parcours que DiagnosticOffers.vue. */
        async onContratAccepted(payload) {
            this.showContratPopup = false;
            // client_apte n'est vrai qu'après paiement réussi (le serveur refuse la signature
            // "souscription" avant) : on met en attente ici, `flushPendingSignatures` (déjà
            // appelé par DashboardLayout au retour sur le tableau de bord) l'enverra ensuite.
            enqueuePendingSignature({
                titre: payload?.documentTitre || 'Contrat de souscription',
                kind: 'souscription',
                signature_image: payload?.image,
                timestamp: payload?.ts || Date.now(),
                email: this.userEmail,
            });
            await this.processPayment();
        },
        async processPayment() {
            this.paymentError = '';
            if (!this.quoteLoaded) {
                this.paymentError = this.quoteError || 'Tarif non chargé.';
                return;
            }
            if (!isAuthenticatedSession()) {
                this.paymentError = "Session expirée. Veuillez vous reconnecter.";
                this.$router.push('/connexion');
                return;
            }

            const channel = PAYMENT_CHANNEL_BY_METHOD[this.selectedMethod];
            if (!channel) {
                this.paymentError = 'Moyen de paiement non pris en charge.';
                return;
            }

            const guarded = await paymentSubmitGuard.run(async () => {
                this.paying = true;
                try {
                    const { data } = await axios.post(
                        '/api/payments/add-payer/',
                        {
                            channel,
                            payment_plan: this.selectedPlan,
                        },
                        { withCredentials: true, timeout: 65000 }
                    );
                    if (data?.amount != null) this.totalAmount = Number(data.amount) || this.totalAmount;
                    trackBeginCheckout({
                        offer: this.offer,
                        plan: this.selectedPlan,
                        value: data?.amount != null ? Number(data.amount) : this.displayAmount,
                    });
                    this.mailSent = true;
                } catch (error) {
                    this.paymentError =
                        formatThrottleMessage(error) ||
                        safeErrorMessage(error, "Impossible d'initier le paiement.");
                } finally {
                    this.paying = false;
                }
            });

            if (!guarded.ok) {
                if (guarded.reason === 'in_flight' || guarded.reason === 'cooldown') {
                    this.paymentError = 'Veuillez patienter avant de relancer le paiement.';
                }
            }
        },
    },
};
</script>

<style scoped>
.payment-shell {
    min-height: 100vh;
    background: var(--paper, #fff);
}

/* En-tête minimal : la page n'a pas de sidebar, il faut un repère et une sortie. */
.payment-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem clamp(1rem, 4vw, 2.5rem);
    border-bottom: 1px solid var(--line);
    background: #fff;
}

.payment-brand {
    display: inline-flex;
    align-items: center;
    margin: 0;
    text-decoration: none;
    line-height: 0;
}

.payment-brand__logo {
    height: 52px;
    width: auto;
    max-width: 180px;
    object-fit: contain;
    display: block;
}

.payment-brand span {
    font-weight: 400;
    color: var(--brand-brown);
}

.payment-exit {
    font-family: var(--font-body);
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--brand-navy);
    text-decoration: none;
    border-bottom: 1px solid transparent;
}

.payment-exit:hover {
    border-bottom-color: var(--brand-gold);
}

.payment-page {
    max-width: 860px;
    margin: 0 auto;
    padding: 2rem 1rem 4rem;
    font-family: var(--font-body);
    color: var(--brand-ink);
}

.payment-page p {
    font-family: var(--font-body);
}

.payment-header {
    text-align: center;
    margin-bottom: 2rem;
}

.payment-title {
    margin: 0 0 0.75rem;
    font-family: var(--font-display);
    font-size: clamp(1.5rem, 3vw, 1.9rem);
    font-weight: 700;
    color: var(--brand-navy);
    line-height: 1.25;
}

.page-description {
    margin: 0 auto;
    max-width: 36rem;
    color: var(--muted);
    line-height: 1.55;
}

.stepper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
}

.stepper__line {
    width: 24px;
    height: 2px;
    background: var(--line);
    flex-shrink: 0;
}

.step-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
    border: 1px solid var(--line);
    color: var(--muted);
    font-size: 0.85rem;
    background: #fff;
}

.step-chip__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 700;
    background: var(--surface);
    color: var(--muted);
}

.step-chip__label {
    font-weight: 600;
}

.step-chip.active {
    border-color: var(--brand-gold);
    color: var(--brand-navy);
    background: var(--brand-gold-soft);
}

.step-chip.active .step-chip__num {
    background: var(--brand-gold);
    color: var(--brand-ink);
}

.step-chip.done {
    border-color: color-mix(in srgb, var(--brand-navy) 35%, transparent);
    color: var(--brand-navy);
}

.step-chip.done .step-chip__num {
    background: var(--brand-navy);
    color: #fff;
}

.card {
    background: #fff;
    border: 1px solid var(--line);
    border-radius: 16px;
    padding: clamp(1rem, 3vw, 1.75rem);
    margin-bottom: 1rem;
}

.section-title {
    margin: 0 0 0.5rem;
    font-family: var(--font-display);
    font-size: 1.2rem;
    color: var(--brand-navy);
}

.muted {
    color: var(--muted);
    margin-bottom: 1rem;
    line-height: 1.5;
}

/* ---- Étape 1 : offres ---- */
.offer-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
}

.offer-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
    padding: 1.15rem 1.1rem;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: #fff;
    cursor: pointer;
    font-family: var(--font-body);
    transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.offer-card:hover:not(:disabled) {
    border-color: var(--brand-gold);
}

.offer-card.active {
    border-color: var(--brand-gold);
    background: var(--brand-gold-soft);
    box-shadow: 0 0 0 1px var(--brand-gold);
}

.offer-card:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.offer-card__badge {
    position: absolute;
    top: -10px;
    right: 12px;
    padding: 2px 10px;
    border-radius: 999px;
    background: var(--brand-navy);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.offer-card__audience {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--brand-brown);
    line-height: 1.3;
}

.offer-card__name {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--brand-navy);
}

.offer-card__price {
    font-size: 0.95rem;
    color: var(--brand-ink);
}

.offer-card__price strong {
    font-size: 1.1rem;
    color: var(--brand-navy);
}

.offer-card__price-muted{
    font-size: 0.7rem;
    color: var(--muted);
}

.offer-card__pitch {
    font-size: 0.88rem;
    color: var(--muted);
    line-height: 1.45;
}

.offer-card__list {
    list-style: none;
    margin: 0.5rem 0 0;
    padding: 0;
    display: grid;
    gap: 4px;
}

.offer-card__list li {
    position: relative;
    padding-left: 1.1rem;
    font-size: 0.85rem;
    line-height: 1.4;
    color: var(--brand-ink);
}

.offer-card__list li::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--brand-gold);
    font-weight: 700;
}

.offer-card__note {
    margin-top: 0.6rem;
    padding-top: 0.6rem;
    border-top: 1px solid var(--line);
    font-size: 0.8rem;
    line-height: 1.4;
    color: var(--brand-brown);
}

.offer-locked-note {
    margin: 1rem 0 0;
    padding: 0.75rem 1rem;
    border-radius: 10px;
    background: var(--surface);
    color: var(--muted);
    font-size: 0.88rem;
}

/* ---- Étape 2 : formules ---- */
.plan-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.plan-btn {
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 0.9rem;
    background: #fff;
    text-align: left;
    cursor: pointer;
    font-family: var(--font-body);
}

.plan-btn.active {
    border-color: var(--brand-gold);
    background: var(--brand-gold-soft);
}

.plan-title {
    display: block;
    font-weight: 600;
    color: var(--brand-ink);
}

.plan-subtitle {
    display: block;
    font-size: 0.85rem;
    color: var(--muted);
    margin-top: 4px;
}

.plan-price {
    display: block;
    margin-top: 0.5rem;
    font-weight: 600;
    color: var(--brand-navy);
    line-height: 1.4;
}

.summary-box {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 0.9rem 1rem;
    margin-bottom: 1.25rem;
}

.summary-box p {
    margin: 0.2rem 0;
}

.summary-box .muted {
    margin: 0.4rem 0 0;
}

.form-group {
    margin-bottom: 0.8rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.2rem;
    font-weight: 600;
    font-family: var(--font-body);
}

.form-group input {
    width: 100%;
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 0.55rem 0.7rem;
    background: #f9fafb;
    font-family: var(--font-body);
}

.form-group input:focus {
    outline: 2px solid var(--brand-gold);
    outline-offset: 1px;
    border-color: var(--brand-gold);
}

.payment-options {
    margin-bottom: 1.5rem;
}

.payment-options__title {
    margin: 0 0 1rem;
    font-family: var(--font-display);
    font-size: 1.05rem;
    color: var(--brand-navy);
}

/* 4 méthodes : OM, Wave, Carte, Virement — 4 colonnes sur grand écran pour tout voir d’un coup */
.payment-methods {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

@media (max-width: 900px) {
    .payment-methods {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.method-btn {
    background: white;
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
    font-family: var(--font-body);
}

.method-btn:hover {
    border-color: var(--brand-gold);
}

.method-btn.active {
    border-color: var(--brand-gold);
    background: var(--brand-gold-soft);
}

.method-btn.method-btn--text {
    min-height: 110px;
    justify-content: center;
}

.method-btn img.method-icon {
    max-width: 88px;
    width: auto;
    height: 44px;
    object-fit: contain;
    margin-bottom: 8px;
}

.method-icon-fa {
    font-size: 2rem;
    color: var(--brand-navy);
    margin-bottom: 8px;
}

.btn-pay {
    background: var(--brand-gold);
    color: var(--brand-ink);
    border: none;
    border-radius: var(--radius-pill);
    padding: 0.9rem 1.75rem;
    font-size: 1rem;
    font-weight: 600;
    font-family: var(--font-body);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: background 0.2s ease, transform 0.2s ease;
}

.btn-secondary {
    border: 1px solid var(--line);
    background: #fff;
    color: var(--brand-ink);
    border-radius: var(--radius-pill);
    padding: 0.7rem 1.1rem;
    cursor: pointer;
    font-family: var(--font-body);
    font-weight: 600;
}

.btn-next {
    background: var(--brand-gold);
    color: var(--brand-ink);
    border: none;
    border-radius: var(--radius-pill);
    padding: 0.7rem 1.25rem;
    cursor: pointer;
    font-family: var(--font-body);
    font-weight: 600;
}

.btn-next:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-next:hover:not(:disabled),
.btn-pay:hover:not(:disabled) {
    background: var(--brand-gold-light);
    transform: translateY(-1px);
}

.btn-pay:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
}

.payment-actions,
.nav-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 1.25rem;
}

.payment-error {
    margin-top: 0.75rem;
    text-align: center;
    color: var(--error);
    font-weight: 600;
}

.rib-box {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 1rem 1.1rem;
    margin: 1rem 0 1.25rem;
}

.rib-title {
    margin: 0 0 0.5rem 0;
    color: var(--brand-navy);
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.rib-intro {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
}

.rib-dl {
    display: grid;
    grid-template-columns: minmax(120px, 160px) 1fr;
    gap: 0.35rem 1rem;
    margin: 0 0 0.75rem;
    font-size: 0.92rem;
}

.rib-dl dt {
    font-weight: 600;
    color: var(--muted);
    margin: 0;
}

.rib-dl dd {
    margin: 0;
    color: var(--brand-ink);
}

.rib-mono {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    font-family: ui-monospace, 'Cascadia Code', monospace;
    word-break: break-all;
}

.rib-row--swift .rib-placeholder {
    font-family: var(--font-body);
    font-size: 0.88rem;
    color: var(--muted);
    line-height: 1.45;
}

.btn-copy {
    flex-shrink: 0;
    border: 1px solid var(--brand-navy);
    background: #fff;
    color: var(--brand-navy);
    border-radius: 6px;
    padding: 0.25rem 0.75rem;
    font-size: 0.8rem;
    cursor: pointer;
    font-family: var(--font-body);
}

.btn-copy:hover {
    background: var(--brand-gold-soft);
    border-color: var(--brand-gold);
}

.btn-outline-full {
    width: 100%;
    border: 1px dashed color-mix(in srgb, var(--brand-navy) 35%, transparent);
    background: #fff;
    color: var(--brand-ink);
    border-radius: 8px;
    padding: 0.65rem 1rem;
    font-size: 0.95rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: var(--font-body);
}

.btn-outline-full:hover {
    border-color: var(--brand-gold);
    color: var(--brand-navy);
}

.copy-feedback {
    margin: 0.5rem 0 0;
    font-size: 0.88rem;
    color: var(--success);
    font-weight: 600;
}

.rib-success {
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #065f46;
    border-radius: 10px;
    padding: 0.85rem 1rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
}

.rib-success i {
    margin-top: 2px;
}

.rib-success p {
    margin: 0 0 0.5rem;
}

.rib-success__link {
    display: inline-block;
    font-weight: 700;
    color: #065f46;
    text-decoration: underline;
}

.rib-success__link:hover {
    color: var(--brand-navy);
}

.btn-pay--secondary {
    background: var(--brand-navy);
    color: #fff;
}

.btn-pay--secondary:hover:not(:disabled) {
    background: var(--brand-navy-deep);
    color: #fff;
}

@media (max-width: 768px) {
    .payment-methods {
        grid-template-columns: 1fr;
    }

    .payment-actions,
    .nav-actions {
        justify-content: stretch;
    }

    .payment-actions button,
    .nav-actions button {
        flex: 1 1 auto;
        justify-content: center;
    }
}
</style>
